// Copied verbatim from tab-portal src/lib/name-identification (qa c50f5268), the portal mirror of
// core-api src/utils/nameIdentification. Rules only: no AI pass. Keep the three in step.
import { COMMON_GIVEN_NAMES } from './constants'
import {
  isHyphenatedSurnameToken,
  isSingleLetterToken,
  normalizeName,
  stripTrailingPersonSuffix,
  tokenize,
} from './normalize'
import { NameOrder, ParsedPersonName } from './types'

const looksLikeGivenName = (token: string): boolean => {
  const upper = token.toUpperCase().replace(/\./g, '')
  if (COMMON_GIVEN_NAMES.has(upper)) return true
  // Title-case or mixed case often indicates given name in client files
  if (/^[A-Z][a-z]+$/.test(token)) return true
  return false
}

/** Courtesy titles written before a name. Not a name part: no field holds them, so they drop. */
const PERSON_TITLES = new Set([
  'MR',
  'MRS',
  'MS',
  'MISS',
  'MX',
  'DR',
  'PROF',
  'REV',
  'HON',
  'SIR',
  'DAME',
  'FR',
])

/** Words that belong to the surname after them: van Beethoven, De La Cruz, von Trapp. */
const SURNAME_PARTICLES = new Set([
  'VAN',
  'VON',
  'DE',
  'DEL',
  'DELLA',
  'DI',
  'DA',
  'DOS',
  'DAS',
  'DU',
  'LA',
  'LE',
  'DER',
  'DEN',
  'TER',
  'TEN',
])

const isTitle = (token: string): boolean =>
  PERSON_TITLES.has(token.toUpperCase().replace(/\./g, ''))

const isParticle = (token: string): boolean =>
  SURNAME_PARTICLES.has(token.toUpperCase())

/**
 * Drop leading titles, "Mr. & Mrs." included, as long as a name is left after them.
 */
const stripLeadingTitles = (tokens: string[]): string[] => {
  let i = 0
  while (
    i < tokens.length &&
    (isTitle(tokens[i]) ||
      (i > 0 && isTitle(tokens[i - 1]) && /^(&|AND)$/i.test(tokens[i]) && isTitle(tokens[i + 1] ?? '')))
  ) {
    i += 1
  }
  return i > 0 && i < tokens.length ? tokens.slice(i) : tokens
}

const parseSurnameFirst = (tokens: string[]): ParsedPersonName => {
  // "VAN BEETHOVEN LUDWIG": the particles open the surname, which ends at the first other word.
  let particles = 0
  while (particles < tokens.length - 2 && isParticle(tokens[particles])) particles += 1
  if (particles > 0) {
    const rest = parseSurnameFirst(tokens.slice(particles))
    return { ...rest, last: [...tokens.slice(0, particles), rest.last].join(' ') }
  }

  if (tokens.length === 0) {
    return { first: '', middle: '', last: '', suffix: '' }
  }

  if (tokens.length === 1) {
    return { first: '', middle: '', last: tokens[0], suffix: '' }
  }

  if (tokens.length === 2) {
    return { first: tokens[1], middle: '', last: tokens[0], suffix: '' }
  }

  if (tokens.length === 3) {
    const [a, b, c] = tokens
    if (isSingleLetterToken(b)) {
      return { first: c, middle: b.replace(/\./g, ''), last: a, suffix: '' }
    }
    if (isSingleLetterToken(c)) {
      return { first: b, middle: c.replace(/\./g, ''), last: a, suffix: '' }
    }
    if (
      tokenLooksLikeGiven(b) &&
      tokenLooksLikeGiven(c) &&
      b.length >= 4 &&
      c.length >= 4
    ) {
      return { first: b, middle: c, last: a, suffix: '' }
    }
    if (tokenLooksLikeGiven(c)) {
      return { first: c, middle: b, last: a, suffix: '' }
    }
    return { first: b, middle: c, last: a, suffix: '' }
  }

  const last = tokens[0]
  return {
    first: tokens[1] ?? '',
    middle: tokens.slice(2).join(' '),
    last,
    suffix: '',
  }
}

const parseGivenFirst = (tokens: string[]): ParsedPersonName => {
  if (tokens.length === 0) {
    return { first: '', middle: '', last: '', suffix: '' }
  }

  if (tokens.length === 1) {
    return { first: tokens[0], middle: '', last: '', suffix: '' }
  }

  if (tokens.length === 2) {
    return { first: tokens[0], middle: '', last: tokens[1], suffix: '' }
  }

  // "Ludwig van Beethoven", "Anne-Marie De La Cruz": particles before the last word join it.
  let lastStart = tokens.length - 1
  while (lastStart > 1 && isParticle(tokens[lastStart - 1])) lastStart -= 1
  if (lastStart < tokens.length - 1) {
    return {
      first: tokens[0],
      middle: tokens.slice(1, lastStart).join(' '),
      last: tokens.slice(lastStart).join(' '),
      suffix: '',
    }
  }

  if (tokens.length === 3 && isSingleLetterToken(tokens[1])) {
    return {
      first: tokens[0],
      middle: tokens[1].replace(/\./g, ''),
      last: tokens[2],
      suffix: '',
    }
  }

  return {
    first: tokens[0],
    middle: tokens.slice(1, -1).join(' '),
    last: tokens[tokens.length - 1],
    suffix: '',
  }
}

/** Parse combined person name using per-file token order. */
export const parsePersonName = (
  raw: string,
  order: NameOrder = 'surname-first',
): ParsedPersonName => {
  const normalized = normalizeName(raw)
  if (!normalized) {
    return { first: '', middle: '', last: '', suffix: '' }
  }

  // Comma convention: "Last, First Middle"
  if (normalized.includes(',')) {
    const [lastPart, givenPart] = normalized.split(',').map((s) => s.trim())
    const givenTokens = stripLeadingTitles(tokenize(givenPart ?? ''))
    const { tokens: givenCore, suffix } = stripTrailingPersonSuffix(givenTokens)
    // Everything after the comma is given names: "Smith, John A" keeps the A as the middle.
    return {
      first: givenCore[0] ?? '',
      middle: givenCore
        .slice(1)
        .map((token) => (isSingleLetterToken(token) ? token.replace(/\./g, '') : token))
        .join(' '),
      last: lastPart,
      suffix,
    }
  }

  let tokens = stripLeadingTitles(tokenize(normalized))
  const { tokens: coreTokens, suffix } = stripTrailingPersonSuffix(tokens)
  tokens = coreTokens

  const parsed =
    order === 'given-first'
      ? parseGivenFirst(tokens)
      : parseSurnameFirst(tokens)

  return { ...parsed, suffix: suffix || parsed.suffix }
}

/** Heuristic: is the first token more likely a surname (POC files are surname-first). */
export const tokenLooksLikeSurname = (token: string): boolean =>
  isHyphenatedSurnameToken(token) ||
  (token.length > 2 &&
    token === token.toUpperCase() &&
    !looksLikeGivenName(token))

export const tokenLooksLikeGiven = looksLikeGivenName
